import React, { useMemo, useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl-next';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { fg } from '@atlaskit/platform-feature-flags';
import { useUIStore } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { MemoLoadingScreen } from '@atlassian/generative-ai-modal/screens/Loading';

import { resetSessionState } from '../../../../pm-plugins/ai-session-context/commands';
import { clearSelectionDecorations } from '../../../../pm-plugins/decoration/actions';
import { useStreamingScroll } from '../../../../pm-plugins/pure-pm-in-editor-direct-streaming/hooks/useStreamingScroll';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { Container } from '../Container';
import { useUserDiscard } from '../hooks/useUserDiscard';
import type { SessionUIComponentProps } from '../SessionUIComponent';
import type { Action } from '../useUIState';

export function LoadingSessionScreen({
	api,
	providerFactory,
	appearance,
	dispatch,
}: SessionUIComponentProps & { dispatch: React.Dispatch<Action> }): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { mounted: isRovoMounted, streaming: isRovoStreaming } = useUIStore();
	const { isInDocumentStreamingEnabled, endExperience, canInlineStreamChanges, editorView } =
		useContentComponentContext();
	const hideLoadingScreen = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		(state) => state.aiExperienceState?.aiDecorationExperiencePluginState.hideLoadingScreen,
	);

	const loadingScreenRef = React.useRef<HTMLDivElement | null>(null);

	const schema = useMemo(() => api?.core.sharedState.currentState()?.schema, [api]);

	const onDiscard = useCallback(() => {
		if (!fg('platform_editor_ai_migrate_from_xstate')) {
			api?.aiExperience.actions.getAiExperienceActor()?.send({ type: 'enable discard' });
		} else {
			api?.core?.actions.execute(resetSessionState());
		}
		dispatch({ type: 'discard' });
	}, [api, dispatch]);

	useEffect(() => {
		if (!fg('platform_editor_ai_migrate_from_xstate')) {
			return;
		}
		// turn off diff when loading screen is shown on first render
		if (api?.trackChanges?.sharedState.currentState()?.isDisplayingChanges) {
			api?.core.actions.execute(api?.trackChanges?.commands.toggleChanges);
		}

		// clear selection decorations on first render
		clearSelectionDecorations(editorView.state)(editorView.state, editorView.dispatch);

		return () => {
			if (!fg('platform_editor_ai_migrate_from_xstate')) {
				return;
			}
			if (!canInlineStreamChanges()) {
				dispatch({ type: 'show-accept-reject' });
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [api, canInlineStreamChanges, dispatch, endExperience]);

	useUserDiscard({ onDiscard, ref: loadingScreenRef });

	useStreamingScroll({ api, view: editorView, modalRef: loadingScreenRef });

	return (
		<Container ref={loadingScreenRef} isChromeless={appearance === 'chromeless'}>
			{schema && (
				<MemoLoadingScreen
					onCancel={onDiscard}
					preview={undefined}
					title={formatMessage(aiExperienceApplicationMessages.loadingTitle)}
					providerFactory={providerFactory}
					schema={schema}
					isStreaming
					canInlineStreamChanges
					hideLoadingScreen={(isRovoMounted && isRovoStreaming) || hideLoadingScreen}
					appearance={appearance}
					isAIFC={isInDocumentStreamingEnabled}
				/>
			)}
		</Container>
	);
}
